import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, Check } from 'lucide-react';

// ============================================================================
// Desplegable con buscador del Gestor (mismo patrón que el selector de
// colaborador de Horas Extra, components/ui/UserPicker.jsx), pero genérico y con
// los tokens de beta (.hxa). Usarlo SIEMPRE en vez de <select> nativo para
// listas de personas, cursos o cualquier lista de más de ~6 opciones.
//
// options: [{ value, label, sub?, color?, photo?, count?, icon? }]
//   color  → punto/inicial de color (instructor)   photo → avatar redondo
//   icon   → componente lucide para opciones especiales ("Todos", "Sin …")
//   count  → número a la derecha (p. ej. cursos del instructor)
// size="sm" → versión compacta para tablas y celdas.
// Funciona dentro de modales/popovers: el panel detiene la propagación de los
// clics (no cierra el modal que lo contiene) y va por encima (z-index alto).
// ============================================================================

const Lead = ({ opt, size = 28 }) => {
    if (opt?.photo) return <img className="hxs-av" src={opt.photo} alt="" style={{ width: size, height: size }} />;
    if (opt?.icon) {
        const Icon = opt.icon;
        return <span className="hxs-av hxs-ico" style={{ width: size, height: size }}><Icon size={size * 0.48} /></span>;
    }
    if (opt?.color) {
        return (
            <span className="hxs-av" style={{ width: size, height: size, background: opt.color, fontSize: size * 0.4 }}>
                {(opt.label || '?').trim().charAt(0).toUpperCase()}
            </span>
        );
    }
    return null;
};

const HxSelect = ({
    value,
    onChange,
    options = [],
    placeholder = 'Seleccionar…',
    searchPlaceholder = 'Buscar…',
    icon: ButtonIcon,
    minWidth = 220,
    popWidth = 300,
    ariaLabel,
    className = '',
    size,
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [active, setActive] = useState(0);
    const [pos, setPos] = useState(null);
    const btnRef = useRef(null);
    const popRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const selected = options.find(o => o.value === value);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return options;
        return options.filter(o => `${o.label} ${o.sub || ''}`.toLowerCase().includes(t));
    }, [options, q]);

    const place = () => {
        const r = btnRef.current?.getBoundingClientRect();
        if (!r) return;
        const width = Math.max(r.width, popWidth);
        const left = Math.min(r.left, window.innerWidth - width - 12);
        const below = window.innerHeight - r.bottom;
        const up = below < 320 && r.top > below;
        setPos({ left: Math.max(12, left), width, top: up ? undefined : r.bottom + 6, bottom: up ? window.innerHeight - r.top + 6 : undefined });
    };

    useEffect(() => {
        if (!open) return undefined;
        place();
        setTimeout(() => inputRef.current?.focus(), 20);
        const onDown = (e) => {
            if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onScroll = (e) => { if (!popRef.current?.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        window.addEventListener('resize', place);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setActive(0); }, [q]);
    useEffect(() => {
        listRef.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [active]);

    const pick = (v) => { onChange(v); setOpen(false); btnRef.current?.focus(); };

    const onKey = (e) => {
        if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, filtered.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter' && filtered[active]) { e.preventDefault(); pick(filtered[active].value); }
    };

    // El popover se monta dentro de .hxa para heredar colores/tema (no en <body>).
    const host = typeof document !== 'undefined' ? (document.querySelector('.hxa') || document.body) : null;

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                className={`hxs-btn${open ? ' open' : ''}${size === 'sm' ? ' sm' : ''} ${className}`}
                style={{ minWidth }}
                disabled={disabled}
                onClick={(e) => { e.stopPropagation(); if (disabled) return; setQ(''); setOpen(v => !v); }}
                onKeyDown={e => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
            >
                {selected && (selected.photo || selected.color || selected.icon)
                    ? <Lead opt={selected} size={size === 'sm' ? 18 : 22} />
                    : ButtonIcon ? <ButtonIcon size={15} className="hxs-bi" /> : null}
                <span className={`hxs-val${selected ? '' : ' ph'}`}>{selected ? selected.label : placeholder}</span>
                <ChevronDown size={15} className="hxs-chev" />
            </button>

            {open && pos && host && createPortal(
                <div
                    ref={popRef}
                    className="hxs-pop"
                    style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
                    role="listbox"
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="hxs-search">
                        <Search size={15} />
                        <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} placeholder={searchPlaceholder} />
                    </div>
                    <div className="hxs-list" ref={listRef}>
                        {filtered.length === 0 ? (
                            <p className="hxs-empty">Sin resultados para “{q}”</p>
                        ) : filtered.map((o, i) => (
                            <button
                                key={String(o.value)}
                                type="button"
                                data-i={i}
                                className={`hxs-opt${o.value === value ? ' sel' : ''}${i === active ? ' act' : ''}`}
                                onMouseEnter={() => setActive(i)}
                                onClick={() => pick(o.value)}
                                role="option"
                                aria-selected={o.value === value}
                            >
                                <Lead opt={o} />
                                <span className="hxs-txt">
                                    <b>{o.label}</b>
                                    {o.sub && <small>{o.sub}</small>}
                                </span>
                                {o.count != null && <span className="hxs-cnt">{o.count}</span>}
                                {o.value === value && <Check size={15} className="hxs-ck" />}
                            </button>
                        ))}
                    </div>
                </div>,
                host
            )}
        </>
    );
};

export default HxSelect;
